import React, { useEffect, useRef, useState } from 'react';
import './App.css';

/**
 * Companion landing page for "How Americans Think About Financial Privacy."
 *
 * STRUCTURE — organized around the three Key Messages (launch deck, slide 4):
 *   01  Bipartisan consensus      — every party answers the same way (chart carousel)
 *   02  A mainstream position     — ~two-thirds want opt-in / no collection
 *   03  The market signal         — privacy is a feature people choose, and the
 *                                    say–do gap is the unbuilt market (chart carousel)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DESIGN PRINCIPLES & COLOR SCHEME (single source of truth = the CSS :root below)
 *
 *  Surfaces      cream (#FAF8F2) page · white (#FFFFFF) cards · alt cream (#F4F1E8)
 *  Text          ink (#1A1814) headings · slate/muted for body and labels
 *  Accent        ONE gold, in two calibrated tints:
 *                  • --gold      #C9A227  → fills & large accents (bars, rule, buttons)
 *                  • --gold-deep #8A6A14  → small gold TEXT + button hover (AA on cream)
 *  Data colors   The SAME scheme drives every chart, one meaning per hue:
 *                  • GOLD  = privacy-positive / "what people say"
 *                  • SLATE = comparison / "what people do"
 *                The party consensus bars are ALL gold so their near-identical
 *                lengths read as agreement — the similarity is the finding.
 *  Motion        bars grow on reveal / page change; off under prefers-reduced-motion.
 *  Type          Inter (UI) + IBM Plex Mono (eyebrows, captions, data values).
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Drop-in src/home.js (CRA `privacy-fedi`). Scoped `fp-` styles. Dependency-free.
 * Logos load from public/ via PUBLIC_URL.
 *
 * TODO before launch:
 *   - REPORT_PDF_URL (final PDF path) and SPONSOR_DISCLOSURE wording (TBD, brief §10).
 *   - Save logos into public/: logo_cornelltpi_2.jpg, logo_bpi.jpg.
 *   - Fedi: supplied file is the DARK-theme mark (black background); shown here as an
 *     ink wordmark. Drop a light-background Fedi logo into public/ to use the image.
 */

const ASSET = process.env.PUBLIC_URL || '';

const REPORT_PDF_URL = `${ASSET}/financial_privacy_report_summer_2026.pdf`;

// All figures trace to the report's Data Appendix (n = 2,200; fielded Mar 3–10, 2026).

// Message 01 — every party-by-party chart, all in the same bar style, fed to the carousel.
const CONSENSUS_CHARTS = [
  {
    title: 'Privacy vs. convenience',
    note: '% favoring privacy',
    rows: [
      { party: 'Republican', v: 59 },
      { party: 'Democrat', v: 61 },
      { party: 'Independent', v: 57 },
    ],
    spread: 4,
  },
  {
    title: 'Privacy vs. crime prevention',
    note: '% favoring privacy',
    rows: [
      { party: 'Republican', v: 50 },
      { party: 'Democrat', v: 51 },
      { party: 'Independent', v: 51 },
    ],
    spread: 1,
  },
  {
    title: '"Me, my bank, and no one else"',
    note: '% who want financial data kept between them and their bank',
    rows: [
      { party: 'Republican', v: 81 },
      { party: 'Democrat', v: 77 },
      { party: 'Independent', v: 78 },
    ],
    spread: 4,
  },
  {
    title: '"Only I should have access."',
    note: '% choosing "only me"',
    rows: [
      { party: 'Republican', v: 59 },
      { party: 'Democrat', v: 55 },
      { party: 'Independent', v: 54 },
    ],
    spread: 5,
  },
  {
    title: '"Never collect my data, or only if I consent"',
    note: '% choosing never / explicit consent',
    rows: [
      { party: 'Republican', v: 70 },
      { party: 'Democrat', v: 66 },
      { party: 'Independent', v: 68 },
    ],
    spread: 4,
  },
];

const STAT_GRID = [
  { fig: '56', desc: 'say only they should have access to their personal financial data' },
  { fig: '68', desc: 'are concerned about the federal government accessing their financial data' },
  { fig: '77', desc: 'took at least one deliberate privacy measure in the past 12 months' },
  { fig: '74', desc: 'would switch or close an account after a serious privacy violation' },
  { fig: '6', desc: 'report coercion, stalking, or physical harm from a financial data breach' },
  { fig: 'Less than 1 in 9', desc: 'respondents accept government access to their financial data even with a warrant', noPct: true },
];

const CONJOINT = [
  { level: 'No external sharing', v: 57.6, best: true },
  { level: 'Shared with service providers', v: 48.7, best: false },
  { level: 'Shared with advertisers', v: 43.9, best: false },
];

const GAP_ROWS = [
  {
    title: 'Privacy over convenience',
    say: { v: 60, text: 'privacy-leaning when forced to choose' },
    did: { v: 4, text: 'regularly use Tor' },
  },
  {
    title: 'Big Tech data collection',
    say: { v: 67, text: 'concerned about Big Tech collecting their data' },
    did: { v: 35, text: 'read a privacy policy this year' },
  },
  {
    title: 'Routine data collection',
    say: { v: 68, text: 'say never, or only with explicit consent' },
    did: { v: 41, text: 'refused to share data with an app' },
  },
  {
    title: 'Credit card data collection',
    // Appendix lists credit-card-company concern at 59% (report's gap chart shows 60%);
    // using 59% per the appendix as designated ground truth.
    say: { v: 59, text: 'concerned about credit card companies' },
    did: { v: 38, text: 'used cash for privacy' },
  },
];

// Reveal-on-scroll for the (non-carousel) conjoint and gap charts; respects reduced motion.
function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setInView(true)),
      { threshold: 0.2 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function LogoChip({ file, alt, h, blend, href }) {
  const [failed, setFailed] = useState(false);
  const inner = failed ? (
    <span className="fp-fedi-word">{alt}</span>
  ) : (
    <img
      className="fp-chip-img"
      src={`${ASSET}/${file}`}
      alt={alt}
      style={{ height: h + 'px', ...(blend ? { mixBlendMode: 'multiply' } : {}) }}
      onError={() => setFailed(true)}
    />
  );
  if (href) {
    return (
      <a className="fp-chip" href={href} target="_blank" rel="noopener noreferrer" aria-label={alt}>
        {inner}
      </a>
    );
  }
  return <span className="fp-chip">{inner}</span>;
}

function BrandBand({ size }) {
  const h = 25;
  return (
    <div className="fp-brandband" aria-label="Research institutions">
      <LogoChip file="logo_cornell_tpi.png" alt="Cornell Tech Policy Institute" h={Math.round(h * 3.15)} href="https://publicpolicy.cornell.edu/btpi/" />
      <LogoChip file="logo_bpi.png" alt="Bitcoin Policy Institute" h={Math.round(h * 1.05)} blend href="https://www.btcpolicy.org/" />
      <LogoChip file="fedibrandmarkdark.avif" alt="Fedi" h={h} blend href="https://fedi.xyz/" />
    </div>
  );
}

// Donut chart for the "two-thirds" hero figure in Key Finding 02.
// Drawn with Canvas 2D — no external library — so it stays within the
// existing dependency-free approach of the rest of the page.
function DonutChart() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const size = 180;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = size + 'px';
    canvas.style.height = size + 'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const cx = size / 2, cy = size / 2, r = 78, inner = 54;
    // segments: [value, color] — gold-deep (35%), gold (33%), light-gray (32%)
    const segments = [
      [79, '#846515'],
      [21, '#E0E0E0'],
    ];
    let start = -Math.PI / 2;
    segments.forEach(([pct, color]) => {
      const sweep = (pct / 100) * 2 * Math.PI;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, start, start + sweep);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      start += sweep;
    });
    // cut the donut hole
    ctx.beginPath();
    ctx.arc(cx, cy, inner, 0, 2 * Math.PI);
    ctx.fillStyle = '#F3F0E9';
    ctx.fill();
  }, []);

  return (
    <div className="fp-donut-wrap" aria-hidden="true">
      <canvas ref={ref} />
      <div className="fp-donut-label">
        <span className="fp-donut-pct">79%</span>
        <span className="fp-donut-sub">of Americans</span>
      </div>
    </div>
  );
}

// Party-bar chart (the "Privacy vs. convenience" style). Bars grow on mount via keyframe,
// so they re-animate each time the carousel lands on their page.
function PartyBars({ title, note, rows, spread }) {
  const aria = rows.map((r) => `${r.party} ${r.v}%`).join(', ');
  return (
    <div className="fp-tradeoff">
      <div className="fp-tradeoff-title">{title}</div>
      <div className="fp-tradeoff-note">{note}</div>
      <div role="img" aria-label={`${title}: ${aria}. Partisan spread ${spread} points.`}>
        {rows.map((r, i) => (
          <div className="fp-barrow" key={r.party}>
            <span className="fp-plabel">{r.party}</span>
            <span className="fp-track">
              <span className="fp-fill fp-pos" style={{ '--w': `${r.v}%`, animationDelay: `${i * 80}ms` }} />
            </span>
            <span className="fp-pval">{r.v}%</span>
          </div>
        ))}
      </div>
      <div className="fp-spread">
        Partisan spread: <b>{spread === 1 ? '1 point' : `${spread} points`}</b>
      </div>
    </div>
  );
}

// Slideshow: two PartyBars per page, with prev/next arrows and dot indicators.
function ConsensusCarousel({ charts, perPage = 2 }) {
  const pages = [];
  for (let i = 0; i < charts.length; i += perPage) pages.push(charts.slice(i, i + perPage));
  const total = pages.length;
  const [page, setPage] = useState(0);
  const go = (d) => setPage((p) => Math.min(Math.max(p + d, 0), total - 1));
  const onKey = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
  };
  const first = page * perPage + 1;
  const last = Math.min(first + perPage - 1, charts.length);
  const countLabel = first === last ? `Chart ${first} of ${charts.length}` : `Charts ${first}–${last} of ${charts.length}`;

  return (
    <div
      className="fp-carousel"
      role="group"
      aria-roledescription="carousel"
      aria-label="Bipartisan consensus charts"
      onKeyDown={onKey}
    >
      <div className="fp-carousel-viewport">
        {/* key={page} remounts the page so the bar-grow animation replays on each move */}
        <div className="fp-tradeoffs" key={page}>
          {pages[page].map((c) => (
            <PartyBars key={c.title} {...c} />
          ))}
        </div>
      </div>

      <div className="fp-carousel-nav">
        <button type="button" className="fp-nav-btn" onClick={() => go(-1)} disabled={page === 0} aria-label="Previous charts">‹</button>
        <div className="fp-cdots">
          {pages.map((_, i) => (
            <button
              type="button"
              key={i}
              className={`fp-dot ${i === page ? 'fp-dot-on' : ''}`}
              onClick={() => setPage(i)}
              aria-label={`Show charts, page ${i + 1} of ${total}`}
              aria-current={i === page ? 'true' : undefined}
            />
          ))}
        </div>
        <button type="button" className="fp-nav-btn" onClick={() => go(1)} disabled={page === total - 1} aria-label="Next charts">›</button>
      </div>

      <p className="fp-carousel-count" aria-live="polite">{countLabel}</p>
    </div>
  );
}

// Generic single-slide carousel — same nav / dots / keyboard / reveal-on-move UI
// as ConsensusCarousel, but one arbitrary slide per page. Used to merge the
// payment-platform conjoint and the say–do-gap charts into one click-through.
function Carousel({ slides, label }) {
  const total = slides.length;
  const [page, setPage] = useState(0);
  const go = (d) => setPage((p) => Math.min(Math.max(p + d, 0), total - 1));
  const onKey = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
  };
  const current = slides[page];

  return (
    <div
      className="fp-carousel"
      role="group"
      aria-roledescription="carousel"
      aria-label={label}
      onKeyDown={onKey}
    >
      <div className="fp-carousel-viewport">
        {/* Render only the active slide, so each page takes its natural height and the
            nav controls sit directly beneath the content — no locked height. key={page}
            remounts the slide so its bar-reveal animation replays on each move. */}
        <div className="fp-slide-stage" key={page}>
          {current.node}
        </div>
      </div>

      <div className="fp-carousel-nav">
        <button type="button" className="fp-nav-btn" onClick={() => go(-1)} disabled={page === 0} aria-label="Previous chart">‹</button>
        <div className="fp-cdots">
          {slides.map((s, i) => (
            <button
              type="button"
              key={s.key}
              className={`fp-dot ${i === page ? 'fp-dot-on' : ''}`}
              onClick={() => setPage(i)}
              aria-label={`Show ${s.label}`}
              aria-current={i === page ? 'true' : undefined}
            />
          ))}
        </div>
        <button type="button" className="fp-nav-btn" onClick={() => go(1)} disabled={page === total - 1} aria-label="Next chart">›</button>
      </div>

      <p className="fp-carousel-count" aria-live="polite">{`Chart ${page + 1} of ${total} · ${current.label}`}</p>
    </div>
  );
}

function StatTile({ fig, desc, noPct }) {
  return (
    <div className="fp-tile">
      <div className="fp-tile-fig">
        {fig}
        {!noPct && <span className="fp-pct">%</span>}
      </div>
      <div className="fp-tile-desc">{desc}</div>
    </div>
  );
}

function ConjointBar({ level, v, best }) {
  const [ref, inView] = useInView();
  return (
    <div className={`fp-cj-row ${inView ? 'fp-in' : ''}`} ref={ref}>
      <span className="fp-cj-level">{level}</span>
      <span className="fp-cj-track">
        <span className="fp-cj-base" aria-hidden="true" />
        <span className={`fp-cj-fill ${best ? 'fp-pos' : 'fp-cmp'}`} style={{ '--w': `${v}%` }} />
      </span>
      <span className="fp-cj-val">{v.toFixed(1)}%</span>
    </div>
  );
}

function GapPair({ title, say, did }) {
  const [ref, inView] = useInView();
  return (
    <div
      className={`fp-pair ${inView ? 'fp-in' : ''}`}
      ref={ref}
      role="img"
      aria-label={`${title}: ${say.v}% ${say.text}; ${did.v}% ${did.text}.`}
    >
      <div className="fp-pair-title">{title}</div>
      <div className="fp-pb">
        <span className="fp-ptrack"><span className="fp-pfill fp-pos" style={{ '--w': `${say.v}%` }} /></span>
        <span className="fp-pnum">{say.v}%</span>
      </div>
      <div className="fp-lab">{say.text}</div>
      <div className="fp-pb">
        <span className="fp-ptrack"><span className="fp-pfill fp-cmp" style={{ '--w': `${did.v}%` }} /></span>
        <span className="fp-pnum">{did.v}%</span>
      </div>
      <div className="fp-lab">{did.text}</div>
    </div>
  );
}

function Legend({ pos, cmp }) {
  return (
    <div className="fp-legend" aria-hidden="true">
      <span className="fp-leg-item"><span className="fp-sw fp-sw-pos" />{pos}</span>
      <span className="fp-leg-item"><span className="fp-sw fp-sw-cmp" />{cmp}</span>
    </div>
  );
}

// ── Key finding 03 slides ─────────────────────────────────────────────────
// One full-width "chart slide" per carousel page. Each slide reuses the existing
// ConjointBar / GapPair / Legend pieces, so the data and styling are identical to
// the standalone charts they replace — only the container and framing change.

function ConjointSlide() {
  return (
    <div className="fp-slide">
      <div className="fp-tradeoff-title">When people choose, they choose privacy</div>
      <div className="fp-tradeoff-note">
        Likelihood of picking a payment platform, by its data-sharing policy
      </div>
      <div className="fp-cj">
        {CONJOINT.map((c) => (
          <ConjointBar key={c.level} {...c} />
        ))}
        <div className="fp-cj-axis" aria-hidden="true">
          <span>40%</span>
          <span className="fp-cj-mid">50% = no effect</span>
          <span>60%</span>
        </div>
        <Legend pos="Most private option" cmp="Other levels" />
      </div>
      <p className="fp-slide-note">
        How data is <em>shared</em> swung choice by about 14 points — statistically tied with price,
        merchant acceptance, and interface quality. Data <em>collected</em> mattered far less (about
        6 points).
      </p>
    </div>
  );
}

function GapSlide({ title, note, rows, wide, footer }) {
  return (
    <div className="fp-slide">
      <div className="fp-tradeoff-title">{title}</div>
      <div className="fp-tradeoff-note">{note}</div>
      <Legend pos="What people say" cmp="What they do" />
      <div className={`fp-pairs ${wide ? 'fp-pairs-row' : ''}`}>
        {rows.map((g) => (
          <GapPair key={g.title} {...g} />
        ))}
      </div>
      {footer && <p className="fp-slide-note">{footer}</p>}
    </div>
  );
}

// Slides for the merged Key finding 03 carousel: the choice experiment first
// (the market signal), then ALL four say–do pairs on one slide (the opportunity).
// Two slides total — the combined gap slide uses a single row so it fits within
// the conjoint slide's height; the stacking deck locks both pages to that height.
const M3_SLIDES = [
  {
    key: 'conjoint',
    label: 'the payment-platform choice experiment',
    node: <ConjointSlide />,
  },
  {
    key: 'gap',
    label: 'the say–do gap across four measures',
    node: (
      <GapSlide
        title="Americans want privacy — but few translate it into action"
        note="Stated preference (gold) outruns reported behavior (slate) — widest where privacy needs a specialized tool"
        rows={GAP_ROWS}
        wide
        footer={
          <>
            Far more people <em>say</em> they value privacy than <em>act</em> on it — widest where
            acting needs a specialized tool. That's friction, not a lack of will.
          </>
        }
      />
    ),
  },
];

// ── Access gate ────────────────────────────────────────────────────────────
// DISABLED: the site is public, no password gate. Set GATE_ENABLED back to
// true to require the password screen below.
const GATE_ENABLED = false;
const GATE_KEY = 'fp-unlocked-v1';
// SHA-256 of the access password. Default password is "change-me-before-launch".
// Replace before sharing — regenerate with:  printf '%s' 'YOURPASSWORD' | shasum -a 256
const PASSWORD_SHA256 = '22d1c9017df0929ef31cfd943bbc97d2adeff40c010e19d89f1a006943f2132c';

async function sha256Hex(text) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function Gate({ onUnlock }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!value) return;
    setBusy(true);
    setError('');
    try {
      const hex = await sha256Hex(value);
      if (hex === PASSWORD_SHA256) {
        try { sessionStorage.setItem(GATE_KEY, '1'); } catch (err) { /* storage may be blocked */ }
        onUnlock();
      } else {
        setError('That password is not correct.');
        setValue('');
      }
    } catch (err) {
      setError('Could not verify the password in this browser.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="fp-gate">
      <form className="fp-gate-card" onSubmit={submit}>
        <p className="fp-eyebrow">Embargoed · not for distribution</p>
        <h1 className="fp-gate-title">How Americans Think About Financial Privacy</h1>
        <p className="fp-gate-text">This preview is password-protected ahead of launch. Enter the access password to continue.</p>
        <label className="fp-gate-label" htmlFor="fp-pw">Access password</label>
        <input
          id="fp-pw"
          className="fp-gate-input"
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete="current-password"
          aria-describedby={error ? 'fp-pw-err' : undefined}
          aria-invalid={error ? 'true' : undefined}
        />
        {error && <p className="fp-gate-err" id="fp-pw-err" role="alert">{error}</p>}
        <button className="fp-btn fp-btn-primary fp-gate-btn" type="submit" disabled={busy || !value}>
          {busy ? 'Checking…' : 'Enter'}
        </button>
      </form>
    </main>
  );
}

function Page() {
  return (
    <>
      {/* MASTHEAD — leading co-brand band */}
      <header className="fp-bar">
        <div className="fp-wrap fp-bar-inner">
          <BrandBand size="lg" />
          <div className="fp-tagline">RESEARCH REPORT · 2026</div>
        </div>
      </header>

      {/* HERO */}
      <section className="fp-hero">
        <div className="fp-wrap">
          <img className="fp-hero-art" src={`${ASSET}/hero-consensus.svg`} alt="" aria-hidden="true" />
          <p className="fp-eyebrow">A national survey of 2,200 U.S. adults</p>
          <h1 className="fp-h1">Financial Privacy: Common Ground in a Country Divided</h1>
          <p className="fp-lede-intro">Americans agree:</p>
          <ul className="fp-lede-list">
            <li>
              <strong>Financial privacy crosses party lines.</strong> Republicans, Democrats, and Independents show nearly the same level of concern about protecting financial data.
            </li>
            <li>
              <strong>Financial privacy is mainstream.</strong> About two-thirds of respondents want their financial data either left uncollected or collected only with explicit consent.
            </li>
            <li>
              <strong>Financial privacy shapes user trust.</strong> When choosing a payment platform, respondents weighed privacy as heavily as price and merchant acceptance.
            </li>
          </ul>
          <div className="fp-actions">
            <a className="fp-btn fp-btn-primary" href={REPORT_PDF_URL}>Download the report (PDF)</a>
            <a className="fp-btn fp-btn-ghost" href="#m1">See the findings</a>
            <a className="fp-btn fp-btn-ghost" href="#implications">Implications</a>
          </div>
          <p className="fp-note">Free to read and share — no email required.</p>
        </div>
      </section>

      {/* MESSAGE 01 — bipartisan consensus (one section, chart carousel) */}
      <section className="fp-band" id="m1" aria-labelledby="m1h">
        <div className="fp-wrap">
          <p className="fp-kicker">Key finding 01 · Bipartisan consensus</p>
          <h2 className="fp-h2" id="m1h">On Privacy, Political Differences Disappear</h2>
          <p className="fp-intro">
            When asked about financial privacy tradeoffs, Republicans, Democrats, and Independents sit within a few points of each other.
          </p>
          <ConsensusCarousel charts={CONSENSUS_CHARTS} />
        </div>
      </section>

      {/* MESSAGE 02 — not a fringe issue */}
      <section className="fp-section fp-section-alt" aria-labelledby="m2h">
        <div className="fp-wrap">
          <p className="fp-kicker">Key finding 02 · A mainstream position</p>
          <h2 className="fp-h2" id="m2h">Financial Privacy Is Not a Fringe Issue</h2>
          <div className="fp-feature">
            <DonutChart />
            <div className="fp-feature-body">
              <p>
                want their financial data limited to <strong>themselves or their bank</strong> — and no one else.
              </p>
              <p className="fp-feature-break">68% say companies should never collect their spending data, or only with explicit consent.</p>
              <div className="fp-donut-legend">
                <span className="fp-donut-legend-item"><span className="fp-donut-legend-dot" style={{background:'#846515'}}></span>Me and my bank only</span>
                <span className="fp-donut-legend-item"><span className="fp-donut-legend-dot" style={{background:'#E0E0E0'}}></span>Broader access accepted</span>
              </div>
            </div>
          </div>
          <div className="fp-grid6">
            {STAT_GRID.map((s) => (
              <StatTile key={s.desc} {...s} />
            ))}
          </div>
        </div>
      </section>

      {/* MESSAGE 03 — the market signal: privacy is a feature people choose, and the
          say–do gap is the unbuilt market. Conjoint + walk-talk gap merged into one
          click-through carousel, mirroring Key finding 01. */}
      <section className="fp-band" id="m3" aria-labelledby="m3h">
        <div className="fp-wrap">
          <p className="fp-kicker">Key finding 03 · The market signal</p>
          <h2 className="fp-h2" id="m3h">Building for Privacy Is Building for Trust</h2>
          <p className="fp-intro">
            Two results converge. In a discrete-choice experiment, how a platform <em>shares</em>{' '}
            data moved choice as much as price and merchant acceptance — privacy is a feature people
            pick. But acting on that preference still takes a specialized tool, and that gap is the
            opportunity.
          </p>
          <Carousel slides={M3_SLIDES} label="Market-signal charts" />
        </div>
      </section>

      {/* IMPLICATIONS */}
      <section className="fp-section fp-section-alt" id="implications" aria-labelledby="impl-h">
        <div className="fp-wrap">
          <p className="fp-kicker">What this means</p>
          <h2 className="fp-h2" id="impl-h">Implications</h2>
          <div className="fp-impl-grid">
            <div className="fp-impl-col">
              <h3 className="fp-impl-h3">For policymakers</h3>
              <ul className="fp-impl-list">
                <li><strong>Financial privacy is a bipartisan issue.</strong> Republicans, Democrats, and Independents all value privacy over convenience and crime prevention. Reform sits where civil liberties, consumer protection, and free-market values converge.</li>
                <li><strong>Reform the system, not the consumer.</strong> Three-quarters of Americans take privacy action each year, but specialized tools see low adoption. The gap is structural, not motivational; privacy must be the default.</li>
                <li><strong>Americans demand opt-in, not opt-out.</strong> 68% say financial data should not be collected, or should require explicit, transaction-level consent. Opt-out regimes do not match what voters want.</li>
                <li><strong>Anti-privacy regulation is anti-consumer regulation.</strong> Americans value payment privacy as much as price and merchant acceptance. Privacy is a competitive feature; suppressing privacy-respecting products suppresses consumer choice.</li>
                <li><strong>Financial privacy is physical safety.</strong> 6% report coercion, stalking, or physical harm from a financial data breach. Mandatory data collection creates predictable victims.</li>
                <li><strong>KYC/AML compliance is a surveillance honeypot.</strong> The Bank Secrecy Act forces institutions to retain sensitive customer data in centralized systems. Every breach hands criminals the financial profiles the rules required companies to collect. Reform should reduce upfront collection, limit retention, and revisit the third-party doctrine.</li>
              </ul>
            </div>
            <div className="fp-impl-col">
              <h3 className="fp-impl-h3">For developers</h3>
              <ul className="fp-impl-list">
                <li><strong>Privacy is a top-line feature, not a compliance afterthought.</strong> Americans weigh data-sharing policy alongside price, merchant acceptance, and interface quality. Build and market privacy as a competitive advantage, not a regulatory backstop.</li>
                <li><strong>The walk-talk gap is the unbuilt market.</strong> 77% of Americans take at least one privacy action each year, but specialized tools see low adoption. The bottleneck is friction, not motivation; the opportunity is privacy by default.</li>
                <li><strong>Make consent meaningful, not buried.</strong> 68% say financial data should not be collected, or should require explicit consent each time. Opt-in builds trust; buried opt-out checkboxes do not.</li>
                <li><strong>Privacy violations are churn events.</strong> 74% say they would stop using an app after a serious privacy violation. Data minimization, breach response, and privacy-by-design are retention infrastructure, not compliance chores.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT THE AUTHORS */}
      <section className="fp-section" id="authors" aria-labelledby="authors-h">
        <div className="fp-wrap">
          <p className="fp-kicker">Who wrote this</p>
          <h2 className="fp-h2" id="authors-h">About the Authors</h2>
          <div className="fp-authors-grid">
            <div className="fp-author">
              <h3 className="fp-author-name">Sarah Kreps</h3>
              <p className="fp-author-bio">Sarah Kreps is Professor and Director of the Tech Policy Institute at Cornell University and Senior Fellow at the Bitcoin Policy Institute.</p>
            </div>
            <div className="fp-author">
              <h3 className="fp-author-name">Renee Sorchik</h3>
              <p className="fp-author-bio">Renee is an expert in translating data into policy, providing easily-digestible content for a diverse range of audiences. She previously worked with various United Nations organizations, and was the former VP of Research at the Bitcoin Policy Institute.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="fp-cta">
        <div className="fp-wrap">
          <h2 className="fp-cta-title">Read the Full Report</h2>
          <p className="fp-cta-lede">
            A national survey of 2,200 U.S. adults, with two discrete-choice experiments. Free to
            read — privacy, after all.
          </p>
          <a className="fp-btn fp-btn-cta fp-btn-lg" href={REPORT_PDF_URL}>Download the report (PDF)</a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="fp-footer">
        <div className="fp-wrap">
          <div className="fp-gold-rule" />
          <BrandBand size="sm" />
        </div>
      </footer>
    </>
  );
}

const Home = () => {
  const [unlocked, setUnlocked] = useState(() => {
    if (!GATE_ENABLED) return true;
    try { return sessionStorage.getItem(GATE_KEY) === '1'; } catch (err) { return false; }
  });
  return (
    <div className="fp-root fp-dots">
      {unlocked ? <Page /> : <Gate onUnlock={() => setUnlocked(true)} />}
    </div>
  );
};


export default Home;